import { create } from "zustand";
import { persist } from "zustand/middleware";
import { loadStepperAdventure } from "@/features/stepper/adventure";
import type { StepperGraph } from "@/features/stepper/types";
import { apiClient } from "@/shared/lib/api-client";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";
import { useFunnelStore } from "./funnel";
import { useMissionProgress } from "./mission-progress";
import { devMissionNo } from "@/features/stepper/dev-mission";
import { queryClient } from "@/app/query-client";
import { fileKeys } from "@/features/funnel/api/use-mission-file";
import { opLink } from "@/features/funnel/hooks/use-phone-login";

const STORAGE_KEY = "secret-missions-stepper";

/** The step a later mission starts on: the intro (key, the big one, the
 *  orders, their name) is for the first mission only. */
const LATER_MISSION_STEP = "Doors";

/**
 * Where a fresh run starts: the graph's opening step for a first mission, the
 * doors for anyone who has filed one already (or, in dev, set a mission
 * number past 1). A failed lookup starts at the opening — never strands them.
 */
/** Which mission this run is: one more than the missions filed (the dev
 *  menu's number when set). Read from the cached file the screens load. */
function missionNumber() {
  const dev = devMissionNo();
  if (dev) return dev;
  const filed = queryClient.getQueryData<unknown[]>([
    ...fileKeys.all,
    opLink(),
  ]);
  return (filed?.length ?? 0) + 1;
}

async function openingStep(graph: StepperGraph, first: string) {
  if (!graph.steps[LATER_MISSION_STEP]) return first;
  const dev = devMissionNo();
  if (dev) return dev > 1 ? LATER_MISSION_STEP : first;
  try {
    const op = opLink();
    const { data } = await apiClient.get<{ result: { missions: unknown[] } }>(
      API_ENDPOINTS.mission.file,
      { params: op ? { op } : undefined },
    );
    return data?.result?.missions?.length ? LATER_MISSION_STEP : first;
  } catch {
    return first;
  }
}

/**
 * Fire-and-forget: log one ai.journey row for the step the visitor just
 * entered. Navigation here is a pure client-side walk, so the stepper backend
 * never sees these steps — this is how the journey gets recorded. Best-effort;
 * a failed log must never block the walk. The backend fills step metadata
 * (channel/type/milestone) from ai.steps and the visitor from the session.
 */
function recordStep(adventureId: string | number, stepName: string) {
  if (!stepName) return;
  apiClient
    .post(API_ENDPOINTS.stepper.journey(adventureId), { stepName })
    .catch(() => {});
}

/** The one adventure this app plays (VITE_STEPPER_ADVENTURE_ID overrides). */
const ADVENTURE_ID: string =
  (import.meta.env.VITE_STEPPER_ADVENTURE_ID as string | undefined) ?? "32";

/**
 * The stepper run: the loaded step graph plus where the visitor is in it.
 * Navigation between screens is a determined-branch walk over this graph —
 * `dispatchFrom(step, eventV)` finds the first branch whose `eventV` matches
 * and moves to its `nextStep`. Copy for each screen comes from the current
 * step's `options`, so the whole flow is JSON-driven.
 *
 * `currentStepName` is persisted (like Glow's `gg_stepper_state`) so a hard
 * refresh resumes on the same step.
 */
interface StepperState {
  graph: StepperGraph | null;
  adventureId: string | number;
  currentStepName: string;
  history: string[];
  /** error: the graph could not be loaded (load() may be called again). */
  status: "idle" | "loading" | "ready" | "error";
  /** The current step was reached with the browser's back button — a screen
   *  can then show where they left it rather than starting over. */
  cameBack: boolean;

  load: () => Promise<void>;
  /** Walk one determined branch from `from`. Returns the new step or null. */
  dispatchFrom: (from: string, eventV: string) => string | null;
  /** Walk from the current step. Returns whether a branch matched. */
  dispatch: (eventV: string) => boolean;
  /** Jump to a named step (URL sync / progress-dot jumps). `back`: the
   *  browser's back button. */
  goTo: (stepName: string, back?: boolean) => void;
  reset: () => void;
}

export const useStepperStore = create<StepperState>()(
  persist(
    (set, get) => ({
      graph: null,
      adventureId: ADVENTURE_ID,
      currentStepName: "",
      history: [],
      status: "idle",
      cameBack: false,

      load: async () => {
        const { status } = get();
        if (status !== "idle" && status !== "error") return;
        set({ status: "loading" });
        let graph: StepperGraph;
        try {
          graph = await loadStepperAdventure(ADVENTURE_ID);
        } catch (e) {
          // a dropped request or a bad graph: say so and let them retry,
          // rather than "loading" for ever
          console.error("[stepper] could not load the adventure", e);
          set({ status: "error" });
          return;
        }

        // `?reset` — start the run over from the opening screen (the first
        // step the server sends; they arrive in sequence order). Same member,
        // so their file keeps earlier debriefs. Without it a finished run
        // reopens on its last screen forever, which is right for an operative
        // coming back and useless when testing.
        const url = new URL(window.location.href);
        // a texted link's member and day: kept (opLink, use-doors) and taken
        // off the address, so a reload days later is not that day's link
        opLink();
        if (url.searchParams.has("op") || url.searchParams.has("d")) {
          url.searchParams.delete("op");
          url.searchParams.delete("d");
          window.history.replaceState(null, "", url);
        }
        if (url.searchParams.has("reset")) {
          for (const key of ["reset", "go", "step", "m", "t"]) {
            url.searchParams.delete(key);
          }
          window.history.replaceState(null, "", url);
          // a mock run keeps its file on the mock server: a reset is the
          // whole run (dev only; the real backend has no such endpoint)
          if (import.meta.env.DEV) {
            await apiClient.post("/api/__mock/reset").catch(() => {});
            queryClient.clear();
          }
          useFunnelStore.getState().resetFunnel();
          // a reset is the whole run: steps ticked on earlier missions too
          useMissionProgress.setState({ done: {} });
          // The position API sorts steps by Sequence/StepId, and the loader
          // preserves that order. graph.firstStep is the saved resume position,
          // so using it here would undo this explicit reset.
          const opening = Object.keys(graph.steps)[0];
          set({
            graph,
            adventureId: graph.adventureId,
            currentStepName: opening,
            history: [],
            status: "ready",
          });
          recordStep(graph.adventureId, opening);
          return;
        }

        // `?m=<MissionID>&t=<accepted, ms>` — the mission-accepted text's link:
        // that mission is the live one (on a device that never saw it
        // accepted, too). The tier is left for the page to read off the
        // mission when it is not the one this browser accepted.
        const m = Number(url.searchParams.get("m"));
        if (m > 0) {
          const t = Number(url.searchParams.get("t"));
          const funnel = useFunnelStore.getState();
          useFunnelStore.setState({
            missionId: m,
            acceptedAt: t > 0 ? t : (funnel.acceptedAt ?? Date.now()),
            tier: funnel.missionId === m ? funnel.tier : null,
          });
          url.searchParams.delete("m");
          url.searchParams.delete("t");
          window.history.replaceState(null, "", url);
        }

        // `?go=<StepName>` — a link we texted that means a particular screen
        // (the morning text: straight to the doors, not the intro again).
        const go = url.searchParams.get("go");
        if (go && graph.steps[go]) {
          url.searchParams.delete("go");
          window.history.replaceState(null, "", url);
          set({
            graph,
            adventureId: graph.adventureId,
            currentStepName: go,
            history: [],
            status: "ready",
          });
          recordStep(graph.adventureId, go);
          return;
        }

        const prev = get();
        // Resume only if the persisted spot still belongs to this graph.
        const sameAdv = String(prev.adventureId) === String(graph.adventureId);
        const resume =
          sameAdv &&
          !!prev.currentStepName &&
          !!graph.steps[prev.currentStepName];
        const currentStepName = resume
          ? prev.currentStepName
          : await openingStep(graph, graph.firstStep);
        set({
          graph,
          adventureId: graph.adventureId,
          currentStepName,
          history: resume ? prev.history : [],
          status: "ready",
        });
        // Log the opening step on a fresh run (a resume already logged it).
        if (!resume) recordStep(graph.adventureId, currentStepName);
      },

      dispatchFrom: (from, eventV) => {
        const { graph } = get();
        const branch = graph?.steps[from]?.branches.find(
          (b) => b.eventV === eventV,
        );
        let next = branch?.nextStep;
        if (!next || !graph?.steps[next]) return null;
        // The handler is given with the fourth mission: any other run walks
        // straight through the choose-your-handler step.
        const nextStep = graph.steps[next];
        if (nextStep.stepType === "WebSmHandler" && missionNumber() !== 4) {
          const through = nextStep.branches[0]?.nextStep;
          if (through && graph.steps[through]) next = through;
        }
        set((s) => ({
          currentStepName: next,
          history: [...s.history, from],
          cameBack: false,
        }));
        recordStep(get().adventureId, next);
        return next;
      },

      dispatch: (eventV) =>
        get().dispatchFrom(get().currentStepName, eventV) !== null,

      goTo: (stepName, back = false) => {
        if (get().graph?.steps[stepName])
          set({ currentStepName: stepName, cameBack: back });
        // the server did not send that step: say so, rather than a dead button
        else console.warn(`[stepper] goTo("${stepName}"): no such step loaded`);
      },

      // Back to the opening screen. Not graph.firstStep: for a returning
      // member the server sends where they left off there (e.g. their live
      // mission), and a scanned key must always start the run from the top.
      reset: () => {
        const { graph } = get();
        set({
          currentStepName: graph ? Object.keys(graph.steps)[0] : "",
          history: [],
          cameBack: false,
        });
      },
    }),
    {
      name: STORAGE_KEY,
      version: 1,
      // Persist only the resume pointer — the graph reloads on boot.
      partialize: (s) => ({
        adventureId: s.adventureId,
        currentStepName: s.currentStepName,
      }),
    },
  ),
);
